import React, { useState } from 'react'
import { JourneyData } from './types'
import { CheckCircle2, Target, Users, User, Database, Coins, Edit3, ShieldCheck, Filter, Calculator, Compass } from 'lucide-react'
import { goalOptions } from '../onboarding/Onboarding'
import { formatIDR, formatCompactIDR } from '../lib/financialCalculation'
import {
  evaluateTargetReadiness,
  EDUCATION_STAGES,
} from '../lib/targetSpecification'
import { evaluateP4Readiness } from '../lib/costFunding'
import {
  getGoalOwnerDisplay,
  formatRelationshipLabel,
} from '../lib/goalCapability'
import { getGoalDomain } from '../lib/goalDomainRegistry'

interface WhatWeKnowCardProps {
  journey: JourneyData
  onEditGoalClick: () => void
  onOpenFinancialReality?: () => void
  onOpenTargetSpecification?: (goalId?: string) => void
  onOpenCostFunding?: (goalId?: string) => void
  onOpenCalculation?: (goalId?: string) => void
  onOpenRealityCheck?: (goalId?: string) => void
  onOpenPetaEsok?: (goalId?: string) => void
  onEditHouseholdName?: () => void
}

export const WhatWeKnowCard: React.FC<WhatWeKnowCardProps> = ({
  journey,
  onEditGoalClick,
  onOpenFinancialReality,
  onOpenTargetSpecification,
  onOpenCostFunding,
  onOpenCalculation,
  onOpenRealityCheck,
  onOpenPetaEsok,
  onEditHouseholdName,
}) => {
  const { household, persons, goals, financialReality, financialStatus } = journey
  const [personFilter, setPersonFilter] = useState<string>('all')

  const activePersons = persons.filter((p) => p.status !== 'archived')

  const formatRelationship = (rel: string) => {
    switch (rel) {
      case 'self':
        return 'Diri Sendiri'
      case 'child':
        return 'Anak'
      case 'spouse':
        return 'Pasangan'
      case 'family':
        return 'Keluarga'
      case 'dependent':
        return 'Tanggungan / Orang Tua'
      case 'household':
        return 'Anggota Lain'
      default:
        return rel
    }
  }

  const hasFinancialData = financialStatus.hasIncomes || financialStatus.hasExpenses

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm text-left space-y-4">
      {/* Card Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-teal-50 text-[#0EA5A4] flex items-center justify-center flex-shrink-0">
            <CheckCircle2 size={16} />
          </div>
          <div>
            <span className="text-[10px] font-bold tracking-wider text-teal-700 uppercase">
              Fakta Terkonfirmasi
            </span>
            <h3 className="text-sm font-bold text-slate-900 leading-tight">
              Yang Sudah Diketahui BekalNanti
            </h3>
          </div>
        </div>
        <button
          type="button"
          onClick={onEditGoalClick}
          className="text-xs font-semibold text-[#0EA5A4] hover:text-[#0F766E] transition-colors cursor-pointer px-2 py-1 rounded-lg hover:bg-teal-50"
        >
          Kelola Tujuan
        </button>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        Berikut adalah informasi awal yang kamu berikan. BekalNanti hanya menggunakan data nyata tanpa berasumsi.
      </p>

      {/* Facts Grid */}
      <div className="space-y-3 pt-1">
        {/* 1. Profil & Keluarga */}
        <div className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/70 text-xs space-y-1.5">
          <div className="flex items-center justify-between text-slate-500">
            <span className="flex items-center gap-1.5 font-medium text-[11px]">
              <Users size={13} className="text-slate-400" />
              <span>Konteks Perencanaan</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-700">{household.name}</span>
              {onEditHouseholdName && (
                <button
                  type="button"
                  onClick={onEditHouseholdName}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-teal-200/80 bg-teal-50/70 hover:bg-teal-100/80 text-[#0F766E] text-[10px] font-bold transition-all cursor-pointer shadow-2xs"
                  title="Ubah nama ruang perencanaan"
                >
                  <Edit3 size={10} className="text-[#0EA5A4]" />
                  <span>Ubah</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="planning-context-list">
            {activePersons.length > 0 ? (
              activePersons.map((p) => {
                const relLabel = formatRelationship(p.relationship)
                const displayLabel =
                  p.fullName.toLowerCase() === relLabel.toLowerCase()
                    ? relLabel
                    : `${p.fullName} (${relLabel})`
                return (
                  <span
                    key={p.id}
                    data-testid="planning-context-badge"
                    data-relationship={p.relationship}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium text-xs"
                  >
                    <User size={11} className="text-[#0EA5A4]" />
                    <span>{displayLabel}</span>
                  </span>
                )
              })
            ) : (
              <span className="text-slate-400 italic">Direncanakan untuk Diri Sendiri</span>
            )}
          </div>
        </div>

        {/* 2. Tujuan Terpilih */}
        <div className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/70 text-xs space-y-2.5">
          <div className="flex items-center justify-between text-slate-500">
            <span className="flex items-center gap-1.5 font-medium text-[11px]">
              <Target size={13} className="text-slate-400" />
              <span>Tujuan yang Disiapkan ({goals.length})</span>
            </span>
            <span className="text-[10px] text-teal-700 font-semibold">Aktif Dipetakan</span>
          </div>

          {/* Context / Person Filter Tabs */}
          {activePersons.length > 0 && goals.length > 1 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 text-xs">
              <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1 flex-shrink-0">
                <Filter size={10} />
                <span>Filter:</span>
              </span>
              <button
                type="button"
                onClick={() => setPersonFilter('all')}
                className={`px-2 py-0.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  personFilter === 'all'
                    ? 'bg-teal-700 text-white shadow-2xs'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                Semua ({goals.length})
              </button>
              {activePersons.map((p) => {
                const count = goals.filter((g) => g.personId === p.id).length
                if (count === 0) return null
                const rel = formatRelationshipLabel(p.relationship)
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPersonFilter(p.id)}
                    className={`px-2 py-0.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      personFilter === p.id
                        ? 'bg-teal-700 text-white shadow-2xs'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {p.fullName} ({rel}) • {count}
                  </button>
                )
              })}
              {goals.some((g) => !g.personId) && (
                <button
                  type="button"
                  onClick={() => setPersonFilter('household')}
                  className={`px-2 py-0.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    personFilter === 'household'
                      ? 'bg-teal-700 text-white shadow-2xs'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  Keluarga • {goals.filter((g) => !g.personId).length}
                </button>
              )}
            </div>
          )}

          {goals.length > 0 ? (
            <div className="space-y-2">
              {goals
                .filter((goal) => {
                  if (personFilter === 'all') return true
                  if (personFilter === 'household') return !goal.personId
                  return goal.personId === personFilter
                })
                .map((goal) => {
                  const opt = goalOptions.find((o) => o.type === goal.goalType)
                  const Icon = opt ? opt.icon : Target
                  const accentColor = opt ? opt.accentColor : '#0EA5A4'
                  const bgLight = opt ? opt.bgLight : '#F0FDFA'

                  const ownerDisplay = getGoalOwnerDisplay(goal, activePersons, journey.archivedPersons)
                  const domain = getGoalDomain(goal.goalType)

                  const target = goal.target
                  const targetReadiness = evaluateTargetReadiness(target, goal.goalType)
                  const stageObj = target?.targetStage
                    ? domain.stages.find((s) => s.id === target.targetStage) || EDUCATION_STAGES.find((s) => s.id === target.targetStage)
                    : null

                  return (
                    <div
                      key={goal.id}
                      className="p-3 rounded-xl bg-white border border-slate-200/90 space-y-2 shadow-2xs"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: bgLight, color: accentColor }}
                          >
                            <Icon size={16} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-slate-800 text-xs truncate leading-snug">
                                {goal.name}
                              </p>
                              <span
                                data-testid="goal-owner-badge"
                                className={`px-1.5 py-0.2 rounded-md text-[10px] font-medium border inline-flex items-center gap-1 ${
                                  ownerDisplay.isArchived
                                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                                    : 'bg-slate-100 text-slate-600 border border-slate-200'
                                }`}
                              >
                                <User size={9} className={ownerDisplay.isArchived ? 'text-amber-600' : 'text-teal-600'} />
                                <span>Untuk: {ownerDisplay.fullNameWithRelation}</span>
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-400 capitalize">
                              Prioritas: {goal.priority === 'high' ? 'Tinggi' : goal.priority === 'low' ? 'Rendah' : 'Menengah'} • Status: {goal.status === 'draft' ? 'Pemetaan Awal' : goal.status}
                            </p>
                          </div>
                        </div>


                        {onOpenTargetSpecification && (
                          <button
                            type="button"
                            onClick={() => onOpenTargetSpecification(goal.id)}
                            className="text-[11px] font-semibold text-[#0EA5A4] hover:text-[#0F766E] inline-flex items-center gap-1 cursor-pointer flex-shrink-0 px-2 py-1 rounded-lg hover:bg-teal-50"
                          >
                            <Edit3 size={11} />
                            <span>{target ? 'Ubah Sasaran' : 'Rincikan Sasaran'}</span>
                          </button>
                        )}
                      </div>

                      {/* Content Body: Target Spec & Cost/Funding for all goals */}
                      <div className="space-y-1.5 pt-1">
                        {/* Target Specification Facts */}
                        {target ? (
                          <div className="pt-1.5 border-t border-slate-100 flex flex-wrap items-center gap-1.5 text-[11px]">
                            <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-800 font-semibold border border-teal-200/60">
                              {stageObj ? stageObj.label : target.targetStage || domain.targetStageLabel}
                            </span>
                            {target.programName && (
                              <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                                {target.programName}
                              </span>
                            )}
                            {(target.targetCity || target.targetCountry) && (
                              <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                                {target.targetCity ? `${target.targetCity}, ` : ''}{target.targetCountry}
                              </span>
                            )}
                            {target.targetYear && (
                              <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                                Thn {target.targetYear}
                              </span>
                            )}
                            {targetReadiness.isComplete ? (
                              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                <CheckCircle2 size={10} />
                                <span>Sasaran Siap Dihitung</span>
                              </span>
                            ) : (
                              <span className="ml-auto text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                Parsial ({targetReadiness.completedFieldsCount}/4)
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400 italic">
                            <span>Spesifikasi sasaran belum dirinci.</span>
                            {onOpenTargetSpecification && (
                              <button
                                type="button"
                                onClick={() => onOpenTargetSpecification(goal.id)}
                                className="text-teal-700 font-semibold not-italic hover:underline cursor-pointer"
                              >
                                Petakan sekarang
                              </button>
                            )}
                          </div>
                        )}

                        {/* Cost & Funding Facts */}
                        {goal.costItems && goal.costItems.length > 0 ? (
                          <div className="pt-1.5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-1.5 text-[11px]">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-800 font-semibold border border-teal-200/60">
                                {goal.costItems.length} Komponen Biaya
                              </span>
                              {goal.fundingSources && goal.fundingSources.length > 0 && (
                                <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                                  {goal.fundingSources.length} Sumber Dana
                                </span>
                              )}
                              {evaluateP4Readiness(goal.costItems || [], goal.fundingSources || [], goal.goalType).isReadyForCalculation ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                  <CheckCircle2 size={10} />
                                  <span>Biaya Siap Dihitung</span>
                                </span>
                              ) : (
                                <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                  Biaya Parsial
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-1.5 ml-auto flex-wrap">
                              {onOpenCalculation && (
                                <button
                                  type="button"
                                  onClick={() => onOpenCalculation(goal.id)}
                                  className="text-[11px] font-bold text-teal-700 hover:text-teal-900 bg-teal-50 border border-teal-200/80 px-2 py-0.5 rounded-lg hover:bg-teal-100 transition-colors inline-flex items-center gap-1 cursor-pointer"
                                >
                                  <Calculator size={11} className="text-[#0EA5A4]" />
                                  <span>Kalkulasi</span>
                                </button>
                              )}
                              {onOpenRealityCheck && (
                                <button
                                  type="button"
                                  onClick={() => onOpenRealityCheck(goal.id)}
                                  className="text-[11px] font-bold text-emerald-800 hover:text-emerald-950 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-lg hover:bg-emerald-100 transition-colors inline-flex items-center gap-1 cursor-pointer"
                                >
                                  <ShieldCheck size={11} className="text-emerald-600" />
                                  <span>Reality Check</span>
                                </button>
                              )}
                              {onOpenPetaEsok && (
                                <button
                                  type="button"
                                  onClick={() => onOpenPetaEsok(goal.id)}
                                  className="text-[11px] font-semibold text-[#0EA5A4] hover:text-[#0F766E] border border-teal-200/70 bg-white px-2 py-0.5 rounded-lg hover:bg-teal-50 transition-colors inline-flex items-center gap-1 cursor-pointer"
                                >
                                  <Compass size={11} className="text-[#0EA5A4]" />
                                  <span>PetaEsok</span>
                                </button>
                              )}
                              {onOpenCostFunding && (
                                <button
                                  type="button"
                                  onClick={() => onOpenCostFunding(goal.id)}
                                  className="text-[11px] font-semibold text-[#0EA5A4] hover:text-[#0F766E] inline-flex items-center gap-1 cursor-pointer hover:underline ml-1"
                                >
                                  <span>Ubah Biaya</span>
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400 italic">
                            <span>Biaya & pendanaan belum dipetakan.</span>
                            {onOpenCostFunding && (
                              <button
                                type="button"
                                onClick={() => onOpenCostFunding(goal.id)}
                                className="text-teal-700 font-semibold not-italic hover:underline cursor-pointer"
                              >
                                Petakan Biaya &amp; Dana
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Belum ada tujuan aktif</p>
          )}
        </div>

        {/* 3. Realita Finansial & Arus Kas */}
        <div className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/70 text-xs space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="flex items-center gap-1.5 font-medium text-[11px]">
              <Coins size={13} className="text-slate-400" />
              <span>Kondisi Keuangan Keluarga</span>
            </span>
            {onOpenFinancialReality && (
              <button
                type="button"
                onClick={onOpenFinancialReality}
                className="text-[11px] font-semibold text-[#0EA5A4] hover:text-[#0F766E] inline-flex items-center gap-1 cursor-pointer"
              >
                <Edit3 size={11} />
                <span>{hasFinancialData ? 'Perbarui Data' : 'Petakan Sekarang'}</span>
              </button>
            )}
          </div>

          {hasFinancialData && financialReality ? (
            <div className="grid grid-cols-2 gap-2 pt-0.5">
              <div className="p-2 rounded-lg bg-white border border-slate-200">
                <span className="text-[10px] text-slate-400 block">Pemasukan Bulanan</span>
                <span className="font-bold text-slate-800 text-xs">
                  {formatCompactIDR(financialReality.summary.totalMonthlyIncome)}
                </span>
              </div>
              <div className="p-2 rounded-lg bg-white border border-slate-200">
                <span className="text-[10px] text-slate-400 block">Pengeluaran &amp; Cicilan</span>
                <span className="font-bold text-slate-800 text-xs">
                  {formatCompactIDR(
                    financialReality.summary.totalMonthlyExpenses +
                      financialReality.summary.totalMonthlyCommitments
                  )}
                </span>
              </div>
              <div className="p-2 rounded-lg bg-teal-50/60 border border-teal-200">
                <span className="text-[10px] text-teal-700 block font-semibold">Kapasitas Bersih Bulanan</span>
                <span
                  className={`font-bold text-xs ${
                    financialReality.summary.netMonthlyCapacity >= 0
                      ? 'text-[#0F766E]'
                      : 'text-amber-700'
                  }`}
                >
                  {formatIDR(financialReality.summary.netMonthlyCapacity)}/bln
                </span>
              </div>
              <div className="p-2 rounded-lg bg-white border border-slate-200">
                <span className="text-[10px] text-slate-400 block">Dana Awal Tersedia</span>
                <span className="font-bold text-slate-800 text-xs">
                  {formatCompactIDR(financialReality.summary.availableSavings)}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-2.5 rounded-lg bg-white border border-dashed border-slate-200 text-slate-400 text-center">
              <span>Arus kas belum tercatat. </span>
              {onOpenFinancialReality && (
                <button
                  type="button"
                  onClick={onOpenFinancialReality}
                  className="text-teal-700 font-bold hover:underline cursor-pointer ml-1"
                >
                  Mulai petakan sekarang
                </button>
              )}
            </div>
          )}
        </div>

        {/* 4. Status Keamanan Data */}
        <div className="p-2.5 rounded-xl bg-teal-50/50 border border-teal-100 flex items-center justify-between text-xs text-teal-800">
          <div className="flex items-center gap-2">
            <Database size={13} className="text-[#0EA5A4] flex-shrink-0" />
            <span className="text-[11px] font-medium">Data tersimpan aman di akun Anda</span>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-100 font-bold uppercase tracking-wider text-[#0F766E]">
            Tersimpan
          </span>
        </div>
      </div>
    </div>
  )
}
